import { getIn, useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import Select, { StylesConfig, FilterOptionOption } from "react-select";
import Creatable from "react-select/creatable";

type Option = {
  label: string;
  value?: string;
  vehiclesValid?: boolean;
  daysAtBase?: number;
};

type ComboBoxFormikProps = {
  name: string;
  label?: string;
  options: Option[];
  className?: string;
  required?: boolean;
  valueProperty?: string;
  allowNewItem?: boolean;
  disableInvalidVehicles?: boolean;
  formatOptionLabel?: (option: Option) => React.ReactNode;
  exactMatchOnly?: boolean;
};

const inputStyles: StylesConfig<{ label: string; value: string }> = {
  control: (style) => ({ ...style, borderColor: '#616161', height: "42px" }),
  option: (styles, { isDisabled }) => ({
    ...styles,
    color: isDisabled ? '#999' : styles.color,
    backgroundColor: isDisabled ? '#f0f0f0' : styles.backgroundColor,
    cursor: isDisabled ? 'not-allowed' : 'default',
    borderLeft: isDisabled ? '3px solid #ff5252' : undefined
  })
};

const AutocompleteFormik: React.FC<ComboBoxFormikProps> = ({
  name,
  label,
  options: initialOptions,
  className,
  required,
  valueProperty,
  allowNewItem,
  disableInvalidVehicles = false,
  formatOptionLabel: customFormatOptionLabel,
  exactMatchOnly = false
}) => {
  const { values, errors, setFieldValue } = useFormikContext();
  const [aditionalOptions, setAditionalOptions] = useState<Option[]>([]);
  const isInvalid = getIn(errors, name);
  const theValueProperty = useMemo(() => valueProperty || 'label', [valueProperty]);
  const currentValue = getIn(values, name);

  const options = useMemo(
    () => [...initialOptions, ...aditionalOptions],
    [initialOptions, aditionalOptions]
  );

  const isOptionDisabled = (option: Option) =>
    disableInvalidVehicles && option.vehiclesValid === false;

  const defaultFormatOptionLabel = (option: Option) => (
    <div>
      {option.label}
      <span className="text-red-500 ml-2">
        {option.daysAtBase != null ? `${option.daysAtBase} DIAS NA BASE` : ''}
      </span>
      {disableInvalidVehicles && option.vehiclesValid === false && (
        <span className="text-red-500 ml-2">(PLV bloqueado)</span>
      )}
    </div>
  );

  const addNewOption = (input: string) => {
    const newOption = {
      label: input,
      [theValueProperty]: input
    } as Option;

    setAditionalOptions((prev) => [...prev, newOption]);
    setFieldValue(name, newOption);
  };

  /**
   * - Considera apenas a parte do label antes do primeiro "|"
   * - Quando exactMatchOnly = true -> precisa ser igual
   * - Quando exactMatchOnly = false -> aceita "startsWith" para UX melhor
   */
  const filterOption = (candidate: FilterOptionOption<Option>, input: string) => {
    if (input.trim() === "") return true;
    const candidateLabel = candidate.data.label || "";
    const candidatePlate = candidateLabel.split("|")[0].trim().toLowerCase();
    const search = input.toLowerCase().trim();

    return exactMatchOnly
      ? candidatePlate === search
      : candidatePlate.includes(search);
  };

  const commonProps = {
    id: name,
    name,
    onChange: (newValue: unknown) => setFieldValue(name, newValue || ''),
    getOptionValue: (item: Option) => (item && item[theValueProperty]) || '',
    value: currentValue,
    className: "w-full",
    ariaRequired: required,
    ariaInvalid: Boolean(isInvalid),
    options,
    styles: inputStyles as Record<string, unknown>,
    isClearable: true,
    isOptionDisabled,
    formatOptionLabel: customFormatOptionLabel || defaultFormatOptionLabel,
    filterOption,
    noOptionsMessage: ({ inputValue }) => exactMatchOnly && inputValue.length < 8 ? "Digite a placa completa: XXX-0X00" : "Nenhum resultado"
  };

  return (
    <div className={`h-20 ${className}`}>
      <label htmlFor={name} className="block text-sm font-medium text-gray-600">
        {label || ''} &nbsp; {required ? <b className="text-redPrimary">*</b> : ' '}
      </label>
      {allowNewItem ? (
        <Creatable {...commonProps} onCreateOption={addNewOption} />
      ) : (
        <Select {...commonProps} />
      )}
      {isInvalid ? (
        <small className="text-redPrimary/90" aria-live="polite">
          {isInvalid}
        </small>
      ) : null}
    </div>
  );
};

export default AutocompleteFormik;

